import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Moon, Sparkles, CalendarDays } from "lucide-react";
import { moonPhase, illumination, phaseKey, nextFullMoon, nextNewMoon, nextSabbat, fmtDate } from "@/lib/moon";
import { SABBAT_INFO } from "@/lib/sabbats";
import MoonDisc from "@/components/MoonDisc";

export const MoonPhase = ({ inline = false }) => {
  const { lang, t } = useLang();
  const now = new Date();
  const phase = moonPhase(now);
  const illum = illumination(phase);
  const nf = nextFullMoon(now);
  const nn = nextNewMoon(now);
  const sabbat = nextSabbat(now);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const daysUntil = Math.round((sabbat.date - today) / 86400000);
  const info = SABBAT_INFO[sabbat.key];

  return (
    <section className={inline ? "mb-14" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-16"} data-testid="moon-phase-widget">
      <div className="mb-8 border-b border-amber-900/30 pb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-amber-500/90 mb-2 flex items-center gap-2"><Moon className="w-3.5 h-3.5" /> {t("moon_subtitle")}</div>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-stone-100">{t("moon_title")}</h2>
        </div>
        <Link to="/lune" data-testid="moon-widget-calendar-link" className="inline-flex items-center gap-1.5 text-sm text-amber-400 hover:text-amber-300 font-mono">
          <CalendarDays className="w-4 h-4" /> {t("lunar_title")} →
        </Link>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Moon card */}
        <div className="flex items-center gap-6 bg-[#121418] border border-[#272A34] rounded-xl p-6 sm:p-8">
          <MoonDisc date={now} size={132} className="shrink-0 drop-shadow-[0_0_18px_rgba(217,169,80,0.25)]" />
          <div>
            <div className="font-serif text-2xl text-amber-100 leading-tight" data-testid="moon-phase-name">{t(phaseKey(phase))}</div>
            <div className="font-mono text-xs text-stone-500 mt-1">{t("moon_illumination")}: <span className="text-amber-400">{Math.round(illum * 100)}%</span></div>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex items-center gap-2 text-stone-400"><span className="w-2 h-2 rounded-full bg-amber-200 inline-block" /> {t("moon_next_full")} · <span className="text-stone-200">{fmtDate(nf, lang)}</span></div>
              <div className="flex items-center gap-2 text-stone-400"><span className="w-2 h-2 rounded-full border border-stone-500 inline-block" /> {t("moon_next_new")} · <span className="text-stone-200">{fmtDate(nn, lang)}</span></div>
            </div>
          </div>
        </div>

        {/* Next sabbat countdown + ritual note */}
        <div className="relative overflow-hidden flex flex-col justify-center bg-[#121418] border border-amber-900/40 rounded-xl p-6 sm:p-8" data-testid="next-sabbat-widget">
          <div className="absolute -right-8 -top-8 opacity-10"><Sparkles className="w-40 h-40 text-amber-500" /></div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/90 mb-2">{t("sabbat_next")}</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <span className="font-serif text-3xl sm:text-4xl text-amber-100" data-testid="next-sabbat-name">{t(`sabbat_${sabbat.key}`)}</span>
            <span className="text-stone-400 text-sm">{fmtDate(sabbat.date, lang)}</span>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-serif text-5xl sm:text-6xl text-amber-400 text-glow" data-testid="sabbat-days">{daysUntil}</span>
            <span className="font-mono text-xs uppercase tracking-wider text-stone-500">{daysUntil === 1 ? t("sabbat_day") : t("sabbat_days")}</span>
          </div>
          {info && <p className="mt-4 text-sm text-stone-400 leading-relaxed max-w-md" data-testid="sabbat-ritual-note"><span className="text-amber-500 font-mono text-[11px] uppercase tracking-wider mr-1">{t("lunar_ritual")}:</span> {info.ritual[lang]}</p>}
        </div>
      </div>
    </section>
  );
};

export default MoonPhase;
